import React, { useState, useRef, useMemo } from "react";
import {
  uid,
  initials,
  badgeClassForState,
  INDUSTRIES,
  CATEGORIES,
  STRATEGIC_SUPPORT,
  EXPANSION_PLANS,
  USE_OF_FUNDS_CATEGORIES,
  VERIFICATION_STATES,
  MOCK_OPPORTUNITIES,
  MOCK_MENTORS,
  PERSONAL_FIELDS,
  CASH_INVESTMENT_CAPACITY_FIELDS,
  CASH_PREFERENCES_FIELDS,
  PAST_INVESTMENT_FIELDS,
  SUCCESS_STORY_FIELDS,
  PORTFOLIO_FIELDS,
  SWEAT_CONTRIBUTION_FIELDS,
  SWEAT_TIME_FIELDS,
  SWEAT_ENGAGEMENT_FIELDS,
  SWEAT_PAST_FIELDS,
  MENTOR_AREA_FIELDS,
  MENTOR_AREAS,
  BUSINESS_BASIC_FIELDS,
  PARTNERSHIP_DETAIL_FIELDS,
  LEGAL_FIELDS,
  FINANCIAL_POSITION_FIELDS,
  LOAN_FIELDS,
  EXISTING_INVESTOR_FIELDS,
  NEGOTIATION_FIELDS,
  CURRENT_OPS_FIELDS,
  FUNDING_FIELDS,
  PROPOSITION_FIELDS,
  MANAGEMENT_FIELDS,
  INVESTOR_TYPE_OPTIONS,
  INVESTOR_PORTAL_NAV,
  SWEAT_PORTAL_NAV,
  MENTOR_PORTAL_NAV,
  BUSINESS_DOCUMENTS,
  HIST_ROWS_INIT,
  YEAR_AGO,
  YEAR_AHEAD,
  CASH_INVESTOR_DECLARATIONS,
  SWEAT_INVESTOR_DECLARATIONS,
  MENTOR_DECLARATIONS,
  PHONE_CODES,
  PARTNERSHIP_STATUS_FIELDS,
  LOAN_STATUS_FIELDS,
  PARTNERSHIP_REVIEW_FIELDS,
  PROJECTION_REVIEW_FIELDS,
  INVESTMENT_DETAILS_REVIEW_FIELDS,
  SWEAT_PERSONAL_REVIEW_FIELDS,
  SWEAT_CONTRIBUTION_REVIEW_FIELDS,
  MENTOR_PERSONAL_REVIEW_FIELDS,
  MENTORING_REVIEW_FIELDS,
  MENTOR_STORY_FIELDS,
} from "../Constants/SharkTank";
import "../Styles/SharkTankStyle.css";
import OpenInvestment from "../../assets/Shark-Tank/Open_Investment.png";
import InvetmentNeed from "../../assets/Shark-Tank/Invetment_Need.png";
import CashInvestorImg from "../../assets/Shark-Tank/Cash Investor.png";
import SweatInvestorImg from "../../assets/Shark-Tank/Sweat_Investor.png";
import MentorImg from "../../assets/Shark-Tank/Mentor.png";

/* ============================================================================
   SHARK TANK — INVESTMENT ECOSYSTEM (FRONTEND ONLY)
   Components + state only. Constants live in Constants/SharkTank.js,
   styling lives in Styles/SharkTankStyle.css (all classes prefixed "Shark").
   ============================================================================ */

/* ---------------------------------------------------------------------------
   UI PRIMITIVES
--------------------------------------------------------------------------- */
function Field({ field, value, onChange, error, data, onChangeData }) {
  const { type, label, required, options, span2, placeholder } = field;
  const cls = "SharkField" + (span2 ? " SharkSpan2" : "");

  if (type === "textarea") {
    const maxWords = field.maxWords || 0;
    const wordCount = maxWords ? (value || "").trim().split(/\s+/).filter(Boolean).length : 0;
    const handleChange = (e) => {
      if (maxWords) {
        const words = e.target.value.trim().split(/\s+/).filter(Boolean);
        if (words.length > maxWords) return;
      }
      onChange(e.target.value);
    };
    return (
      <div className={cls}>
        <label className="SharkLabel">{label}{required && <span className="SharkReq"> *</span>}</label>
        <div className="SharkTextareaWrap">
          <textarea className={"SharkTextarea" + (maxWords ? " SharkTextareaHasCount" : "")} placeholder={placeholder || ""} value={value || ""} onChange={handleChange} />
          {maxWords > 0 && (
            <span className="SharkWordCount">{wordCount}/{maxWords}</span>
          )}
        </div>
        {error && <span className="SharkError">{error}</span>}
      </div>
    );
  }
  if (type === "select") {
    return (
      <div className={cls}>
        <label className="SharkLabel">{label}{required && <span className="SharkReq"> *</span>}</label>
        <select className="SharkSelect" value={value || ""} onChange={(e)=>onChange(e.target.value)}>
          <option value="">Select…</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {error && <span className="SharkError">{error}</span>}
      </div>
    );
  }
  if (type === "radio") {
    return (
      <div className={cls}>
        <label className="SharkLabel">{label}{required && <span className="SharkReq"> *</span>}</label>
        <div className="SharkRadioRow">
          {options.map(o => (
            <div key={o} className={"SharkPill" + (value === o ? " SharkActive" : "")} role="radio" tabIndex={0} aria-checked={value===o}
              onClick={()=>onChange(o)} onKeyDown={(e)=>{ if(e.key==="Enter"||e.key===" ") onChange(o); }}>
              {o}
            </div>
          ))}
        </div>
        {error && <span className="SharkError">{error}</span>}
      </div>
    );
  }
  if (type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter(x=>x!==o) : [...arr, o]);
    return (
      <div className={cls + " SharkSpan2"}>
        <label className="SharkLabel">{label}{required && <span className="SharkReq"> *</span>}</label>
        <div className="SharkCheckRow">
          {options.map(o => (
            <div key={o} className={"SharkPill" + (arr.includes(o) ? " SharkActive" : "")} role="checkbox" tabIndex={0} aria-checked={arr.includes(o)}
              onClick={()=>toggle(o)} onKeyDown={(e)=>{ if(e.key==="Enter"||e.key===" ") toggle(o); }}>
              {o}
            </div>
          ))}
        </div>
        {error && <span className="SharkError">{error}</span>}
      </div>
    );
  }
  if (type === "tel-country") {
    const codeKey = field.codeKey || "mobileCode";
    const codeVal = (data && data[codeKey]) || "+91";
    return (
      <div className={cls}>
        <label className="SharkLabel">{label}{required && <span className="SharkReq"> *</span>}</label>
        <div className="SharkTelCountryWrap">
          <select className="SharkTelCountryCode" value={codeVal} onChange={(e)=>onChangeData(codeKey, e.target.value)}>
            {PHONE_CODES.map((c,i) => <option key={i} value={c.code}>{c.label}</option>)}
          </select>
          <input className="SharkInput SharkTelCountryInput" type="tel" placeholder={placeholder || ""} value={value || ""}
            onChange={(e)=>onChange(e.target.value)} />
        </div>
        {error && <span className="SharkError">{error}</span>}
      </div>
    );
  }
  if (type === "select-other") {
    const otherKey = field.otherKey;
    const otherVal = otherKey ? (typeof data === 'object' && data !== null ? data[otherKey] || "" : "") : "";
    return (
      <div className={cls}>
        <label className="SharkLabel">{label}{required && <span className="SharkReq"> *</span>}</label>
        <select className="SharkSelect" value={value || ""} onChange={(e)=>onChange(e.target.value)}>
          <option value="">Select…</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {value === "Other" && otherKey && (
          <input className="SharkInput SharkMt8" type="text" placeholder="Please specify…" value={otherVal}
            onChange={(e) => onChangeData(otherKey, e.target.value)} />
        )}
        {error && <span className="SharkError">{error}</span>}
      </div>
    );
  }
  return (
    <div className={cls}>
      <label className="SharkLabel">{label}{required && <span className="SharkReq"> *</span>}</label>
      <input className="SharkInput" type={type} placeholder={placeholder || ""} value={value || ""} onChange={(e)=>onChange(e.target.value)} />
      {error && <span className="SharkError">{error}</span>}
    </div>
  );
}

function FormGrid({ fields, data, onChange, errors }) {
  return (
    <div className="SharkFormGrid">
      {fields.map(f => (
        <Field key={f.name} field={f} value={data[f.name]} error={errors && errors[f.name]}
          onChange={(v)=>onChange(f.name, v)} data={data} onChangeData={(k,v)=>onChange(k, v)} />
      ))}
    </div>
  );
}

function SectionCard({ eyebrow, title, hint, children }) {
  return (
    <div className="SharkCard SharkMt24">
      <div className="SharkSectionHead">
        {eyebrow && <div className="SharkEyebrow">{eyebrow}</div>}
        <h3 className="SharkH3">{title}</h3>
        {hint && <p className="SharkMuted">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function FileUpload({ label, fileName, onChange, onRemove }) {
  const inputRef = useRef(null);
  return (
    <div className="SharkUpload">
      <span className="SharkLabel">{label}</span>
      {!fileName ? (
        <div className="SharkUploadBox">
          <span className="SharkUploadEmpty">No file selected</span>
          <button type="button" className="SharkBtn SharkBtnSecondary SharkBtnSm" onClick={()=>inputRef.current.click()}>Choose File</button>
        </div>
      ) : (
        <div className="SharkUploadBox SharkUploadFilled">
          <span className="SharkUploadName">{fileName}</span>
          <div className="SharkUploadActions">
            <button type="button" className="SharkBtn SharkBtnSecondary SharkBtnSm" onClick={()=>inputRef.current.click()}>Replace</button>
            <button type="button" className="SharkBtn SharkBtnText SharkBtnSm" onClick={onRemove}>Remove</button>
          </div>
        </div>
      )}
      <input ref={inputRef} type="file" hidden onChange={(e)=>{ const f=e.target.files[0]; if(f) onChange(f.name); }} />
    </div>
  );
}

function Badge({ state }) {
  return <span className={"SharkBadge " + badgeClassForState(state)}>{state}</span>;
}

function Avatar({ name }) {
  return <div className="SharkAvatar">{initials(name || "?")}</div>;
}

/* Repeatable list of sub-forms (past investments, loans, existing investors, etc.) */
function RepeatableGroup({ title, fields, items, setItems, addLabel }) {
  const update = (id, name, value) => setItems(items.map(it => it.id === id ? { ...it, [name]: value } : it));
  const remove = (id) => setItems(items.filter(it => it.id !== id));
  const add = () => setItems([...items, { id: uid() }]);
  return (
    <div className="SharkMt16">
      {items.map((item, idx) => (
        <div key={item.id} className="SharkCardFlat SharkMt16">
          <div className="SharkFlexBetween">
            <strong>{title} #{idx+1}</strong>
            <button type="button" className="SharkBtn SharkBtnText SharkBtnSm SharkBtnRemove" onClick={()=>remove(item.id)} title="Remove">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
            </button>
          </div>
          <div className="SharkMt16">
            <FormGrid fields={fields} data={item} onChange={(n,v)=>update(item.id,n,v)} errors={{}} />
          </div>
        </div>
      ))}
      <div className="SharkTextRight">
        <button type="button" className="SharkBtn SharkBtnSecondary SharkBtnSm SharkMt16" onClick={add}>+ Add</button>
      </div>
    </div>
  );
}

/* Fixed 5-row financial table (historical or projected) */
function FinancialTable({ title, rows, setRows, yearLabelFn, editableYear }) {
  const update = (idx, key, val) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], [key]: val };
    setRows(next);
  };
  return (
    <div className="SharkMt16">
      <h4 className="SharkH3 SharkTableHeading">{title}</h4>
      <div className="SharkTableWrap SharkMt8">
        <table className="SharkTable">
          <thead>
            <tr>
              <th>{editableYear ? "Year" : "Financial Year"}</th>
              <th className="SharkTableHeadCenter">Revenue</th>
              <th className="SharkTableHeadCenter">Gross Profit</th>
              <th className="SharkTableHeadCenter">Net Profit</th>
              <th className="SharkTableHeadCenter">Employees</th>
              <th className="SharkTableHeadCenter">Offices</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx}>
                <td>{editableYear
                  ? <input className="SharkInput" value={r.year || ""} onChange={(e)=>update(idx,"year",e.target.value)} placeholder={yearLabelFn(idx)} />
                  : yearLabelFn(idx)}</td>
                <td className="SharkTableRowCenter"><input className="SharkInput" type="number" value={r.revenue || ""} onChange={(e)=>update(idx,"revenue",e.target.value)} /></td>
                <td className="SharkTableRowCenter"><input className="SharkInput" type="number" value={r.grossProfit || ""} onChange={(e)=>update(idx,"grossProfit",e.target.value)} /></td>
                <td className="SharkTableRowCenter"><input className="SharkInput" type="number" value={r.netProfit || ""} onChange={(e)=>update(idx,"netProfit",e.target.value)} /></td>
                <td className="SharkTableRowCenter"><input className="SharkInput" type="number" value={r.employees || ""} onChange={(e)=>update(idx,"employees",e.target.value)} /></td>
                <td className="SharkTableRowCenter"><input className="SharkInput" type="number" value={r.offices || ""} onChange={(e)=>update(idx,"offices",e.target.value)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Use of funds table with 100% validation */
function UseOfFundsTable({ rows, setRows }) {
  const update = (idx, key, val) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], [key]: val };
    setRows(next);
  };
  const total = rows.reduce((s, r) => s + (parseFloat(r.percentage) || 0), 0);
  return (
    <div className="SharkMt16">
      <div className="SharkTableWrap">
        <table className="SharkTable">
          <thead><tr><th>Use of Funds</th><th>Amount</th><th>Percentage</th></tr></thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.category}>
                <td>{r.category}</td>
                <td><input className="SharkInput" type="number" value={r.amount||""} onChange={(e)=>update(idx,"amount",e.target.value)} /></td>
                <td><input className="SharkInput" type="number" value={r.percentage||""} onChange={(e)=>update(idx,"percentage",e.target.value)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={"SharkMt8 " + (total === 100 ? "SharkHint" : "SharkError")}>
        Total allocated: {total}% {total !== 100 && "— must total 100% before submission."}
      </p>
    </div>
  );
}

/* Ownership table with 100% validation */
function OwnershipSection({ data, onChange }) {
  const keys = [["founder","Founder Ownership %"],["cofounder","Co-Founder Ownership %"],["partner","Partner Ownership %"],["investor","Investor Ownership %"],["esop","Employee / ESOP Ownership %"],["other","Other Shareholders %"]];
  const total = keys.reduce((s,[k]) => s + (parseFloat(data[k]) || 0), 0);
  return (
    <div>
      <div className="SharkFormGrid">
        {keys.map(([k,label]) => (
          <Field key={k} field={{name:k,label,type:"number"}} value={data[k]} onChange={(v)=>onChange(k,v)} />
        ))}
      </div>
      <p className={"SharkMt8 " + (total === 100 ? "SharkHint" : "SharkError")}>
        Total ownership: {total}% {total !== 100 && "— must total 100% before submission."}
      </p>
    </div>
  );
}

/* Common verification / consent block used by every form */
function CommonVerification({ agree, setAgree, references, setReferences }) {
  return (
    <SectionCard eyebrow="Common Verification" title="Declaration & Consent">
      <div className="SharkFormGrid">
        <Field field={{name:"references",label:"References (optional)",type:"textarea",span2:true,placeholder:"Names and contact details of professional references"}}
          value={references} onChange={setReferences} />
      </div>
      <div className="SharkAgreeRow">
        <input type="checkbox" id="agree" className="SharkCheckbox" checked={agree} onChange={(e)=>setAgree(e.target.checked)} />
        <label htmlFor="agree" className="SharkBody">
          I confirm that the information submitted is accurate and may be reviewed by the IHN Team for verification. <strong>I agree</strong>
        </label>
      </div>
    </SectionCard>
  );
}

/* ---------------------------------------------------------------------------
   FORM COMPONENTS
--------------------------------------------------------------------------- */
function CashInvestorDetails({ formData, setFormData, errors }) {
  const set = (k,v) => setFormData({ ...formData, [k]: v });
  return (
    <>
      <SectionCard title="Personal Information">
        <FormGrid fields={PERSONAL_FIELDS()} data={formData} onChange={set} errors={errors} />
      </SectionCard>
      <SectionCard title="Investment Capacity">
        <FormGrid fields={CASH_INVESTMENT_CAPACITY_FIELDS} data={formData} onChange={set} errors={errors} />
      </SectionCard>
    </>
  );
}

function CashInvestorPreferences({ formData, setFormData, errors }) {
  const set = (k,v) => setFormData({ ...formData, [k]: v });
  return (
    <>
      <SectionCard title="Investment Preferences">
        <FormGrid fields={CASH_PREFERENCES_FIELDS} data={formData} onChange={set} errors={errors} />
      </SectionCard>
      <SectionCard title="Current Portfolio & Strategic Support" hint="Apart from capital, what can you contribute?">
        <FormGrid fields={PORTFOLIO_FIELDS} data={formData} onChange={set} errors={errors} />
      </SectionCard>
      <SectionCard title="Past Investments & Success Stories" hint="Most successful investment, what made it successful, major milestone, approximate return/outcome (optional), exit/acquisition information (if applicable).">
        {formData.hasPastInvestments === "Yes" &&
          <RepeatableGroup title="Investment" fields={PAST_INVESTMENT_FIELDS}
            items={formData.pastInvestments || []} setItems={(items)=>set("pastInvestments", items)}
            addLabel="Add Another Investment" />}
        <RepeatableGroup title="Success Story" fields={SUCCESS_STORY_FIELDS}
          items={formData.successStories || []} setItems={(items)=>set("successStories", items)}
          addLabel="Add Another Story" />
      </SectionCard>
    </>
  );
}

function SweatInvestorDetails({ formData, setFormData, errors }) {
  const set = (k,v) => setFormData({ ...formData, [k]: v });
  return (
    <SectionCard title="Professional Profile">
      <FormGrid fields={[...PERSONAL_FIELDS(true).map(f => f.name === "expertise" ? {...f, span2:false} : f), { name:"coreSkills", label:"Core Skills", type:"text", required:true }]}
        data={formData} onChange={set} errors={errors} />
    </SectionCard>
  );
}

function SweatInvestorContribution({ formData, setFormData, errors }) {
  const set = (k,v) => setFormData({ ...formData, [k]: v });
  return (
    <>
      <SectionCard title="Contribution, Time & Engagement">
        <FormGrid fields={[...SWEAT_CONTRIBUTION_FIELDS, ...SWEAT_TIME_FIELDS, ...SWEAT_ENGAGEMENT_FIELDS, { name:"mentoringAreas", label:"Mentoring Areas", type:"select-other", options:MENTOR_AREAS, required:true, otherKey:"mentoringAreasOther" }, { name:"hasMentored", label:"Have you previously mentored founders/businesses?", type:"select-other", options:["Yes","No","Other"], required:true, otherKey:"hasMentoredOther" }, { name:"hasPrevExperience", label:"Have you previously helped businesses without direct cash investment?", type:"select", options:["Yes","No"] }, { name:"equityPreference", label:"Are you willing to receive equity in exchange for your contribution?", type:"select", options:["Yes","No","Open to Discussion"]}]} data={formData} onChange={set} errors={errors} />
        {formData.hasPrevExperience === "Yes" &&
          <RepeatableGroup title="Engagement" fields={SWEAT_PAST_FIELDS}
            items={formData.pastExperience || []} setItems={(items)=>set("pastExperience", items)}
            addLabel="Add Another Engagement" />}
        {(formData.equityPreference === "Yes" || formData.equityPreference === "Open to Discussion") && (
          <div className="SharkMt16">
            <FormGrid fields={[
              { name:"equityRange", label:"Preferred Equity Range", type:"text" },
              { name:"vestingPeriod", label:"Preferred Vesting Period", type:"text" },
              { name:"involvementDuration", label:"Expected Involvement Duration", type:"text" },
              { name:"expectedResponsibilities", label:"Expected Responsibilities", type:"textarea", span2:true },
            ]} data={formData} onChange={set} errors={errors} />
          </div>
        )}
      </SectionCard>
      <SectionCard title="Business Success Stories" hint="Revenue growth, market expansion, cost reduction, new partnerships, product development, turnaround, team building, fundraising support, or other measurable outcomes.">
        <RepeatableGroup title="Success Story" fields={SUCCESS_STORY_FIELDS}
          items={formData.successStories || []} setItems={(items)=>set("successStories", items)}
          addLabel="Add Another Story" />
      </SectionCard>
    </>
  );
}

function MentorDetails({ formData, setFormData, errors }) {
  const set = (k,v) => setFormData({ ...formData, [k]: v });
  return (
    <SectionCard title="Profile Info">
      <FormGrid fields={PERSONAL_FIELDS(false).map(f => f.name === "expertise" ? {...f, span2:false} : f)} data={formData} onChange={set} errors={errors} />
    </SectionCard>
  );
}

function MentorContribution({ formData, setFormData, errors }) {
  const set = (k,v) => setFormData({ ...formData, [k]: v });
  return (
    <>
      <SectionCard title="Mentoring Areas & Experience">
        <FormGrid fields={[...MENTOR_AREA_FIELDS, { name:"hasMentored", label:"Have you previously mentored founders/businesses?", type:"select", options:["Yes","No"]}]} data={formData} onChange={set} errors={errors} />
      </SectionCard>
      <SectionCard title="Professional / Business Success Stories">
        <RepeatableGroup title="Success Story" fields={SUCCESS_STORY_FIELDS}
          items={formData.successStories || []} setItems={(items)=>set("successStories", items)}
          addLabel="Add Another Story" />
      </SectionCard>
    </>
  );
}

function BusinessDetails({ formData, setFormData, errors }) {
  const set = (k,v) => setFormData({ ...formData, [k]: v });
  return (
    <>
      <SectionCard title="Business Basic Information">
        <FormGrid fields={BUSINESS_BASIC_FIELDS} data={formData} onChange={set} errors={errors} />
      </SectionCard>

      <SectionCard title="Future 5-Year Projection">
        <FinancialTable title="Projected Financials" rows={formData.projection || HIST_ROWS_INIT()} setRows={(r)=>set("projection", r)} yearLabelFn={YEAR_AHEAD} editableYear={false} />
        <div className="SharkFormGrid SharkMt16">
          <Field field={{name:"vision5yr", label:"What is your 5-year vision?", type:"textarea", span2:true, maxWords:200}} value={formData.vision5yr} onChange={(v)=>set("vision5yr", v)} />
          <Field field={{name:"growthDrivers", label:"What will drive the projected growth?", type:"textarea", span2:true, maxWords:200}} value={formData.growthDrivers} onChange={(v)=>set("growthDrivers", v)} />
          <Field field={{name:"expansionPlans", label:"Expansion Plans", type:"select-other", options:EXPANSION_PLANS, otherKey:"expansionPlansOther"}} value={formData.expansionPlans} onChange={(v)=>set("expansionPlans", v)} />
        </div>
      </SectionCard>
    </>
  );
}

function BusinessStatus({ formData, setFormData, errors }) {
  const set = (k,v) => setFormData({ ...formData, [k]: v });
  return (
    <>
      <SectionCard title="Partnership, Loans & Investment">
        <FormGrid fields={PARTNERSHIP_STATUS_FIELDS} data={formData} onChange={set} errors={errors} />
        {formData.isPartnership === "Yes" && (
          <div className="SharkMt16">
            <FormGrid fields={PARTNERSHIP_DETAIL_FIELDS} data={formData} onChange={set} errors={errors} />
            <div className="SharkMt16">
              <FileUpload label="Partnership Deed Upload" fileName={formData.partnershipDeedFile}
                onChange={(name)=>set("partnershipDeedFile", name)} onRemove={()=>set("partnershipDeedFile", "")} />
            </div>
          </div>
        )}
        {formData.hasLoans === "Yes" &&
          <RepeatableGroup title="Loan" fields={LOAN_FIELDS} items={formData.loans || []} setItems={(items)=>set("loans", items)} addLabel="Add Another Loan" />}
        <div className="SharkMt16">
          <Field field={LOAN_STATUS_FIELDS[0]}
            value={formData.otherLiabilities} onChange={(v)=>set("otherLiabilities", v)} />
        </div>
        {formData.hasExistingInvestment === "Yes" &&
          <RepeatableGroup title="Investor" fields={EXISTING_INVESTOR_FIELDS} items={formData.existingInvestors || []} setItems={(items)=>set("existingInvestors", items)} addLabel="Add Another Investor" />}
        {formData.inNegotiation === "Yes" && (
          <div className="SharkMt16"><FormGrid fields={NEGOTIATION_FIELDS} data={formData} onChange={set} errors={errors} /></div>
        )}
      </SectionCard>
      <SectionCard title="Current Business Operations">
        <FormGrid fields={CURRENT_OPS_FIELDS} data={formData} onChange={set} errors={errors} />
      </SectionCard>
    </>
  );
}

function BusinessHistory({ formData, setFormData, errors }) {
  const set = (k,v) => setFormData({ ...formData, [k]: v });
  return (
    <>
      <SectionCard title="Ownership / Shareholding" hint="Percentages must total 100%.">
        <OwnershipSection data={formData.ownership || {}} onChange={(k,v)=>set("ownership", { ...(formData.ownership||{}), [k]: v })} />
      </SectionCard>
      <SectionCard title="Historical Performance — Last 5 Financial Years">
        <FinancialTable title="Historical Financials" rows={formData.historical || HIST_ROWS_INIT()} setRows={(r)=>set("historical", r)} yearLabelFn={YEAR_AGO} editableYear={false} />
      </SectionCard>
    </>
  );
}

function BusinessDocuments({ formData, setFormData, errors }) {
  const set = (k,v) => setFormData({ ...formData, [k]: v });
  return (
    <>
      <SectionCard title="Legal / Regulatory Information">
        <FormGrid fields={LEGAL_FIELDS} data={formData} onChange={set} errors={errors} />
        <div className="SharkFormGrid SharkMt16">
          <FileUpload label="Certificate of Incorporation / Registration" fileName={formData.certFile} onChange={(n)=>set("certFile",n)} onRemove={()=>set("certFile","")} />
          <FileUpload label="GST Certificate" fileName={formData.gstFile} onChange={(n)=>set("gstFile",n)} onRemove={()=>set("gstFile","")} />
          <FileUpload label="PAN / Company Documents" fileName={formData.panFile} onChange={(n)=>set("panFile",n)} onRemove={()=>set("panFile","")} />
          <FileUpload label="Relevant Licenses" fileName={formData.licenseFile} onChange={(n)=>set("licenseFile",n)} onRemove={()=>set("licenseFile","")} />
        </div>
      </SectionCard>

      <SectionCard title="Management Team">
        <FormGrid fields={MANAGEMENT_FIELDS} data={formData} onChange={set} errors={errors} />
        <div className="SharkFormGrid SharkMt16">
          <FileUpload label="Founder Profile" fileName={formData.founderProfileFile} onChange={(n)=>set("founderProfileFile",n)} onRemove={()=>set("founderProfileFile","")} />
          <FileUpload label="Management Profiles / CVs" fileName={formData.mgmtProfilesFile} onChange={(n)=>set("mgmtProfilesFile",n)} onRemove={()=>set("mgmtProfilesFile","")} />
        </div>
      </SectionCard>

      <SectionCard title="Business Documents">
        <div className="SharkFormGrid">
          {BUSINESS_DOCUMENTS.map(doc => (
            <FileUpload key={doc} label={doc} fileName={(formData.docs||{})[doc]}
              onChange={(n)=>set("docs", { ...(formData.docs||{}), [doc]: n })}
              onRemove={()=>{ const d={...(formData.docs||{})}; delete d[doc]; set("docs", d); }} />
          ))}
        </div>
      </SectionCard>

      <div className="SharkCardFlat SharkMt24">
        <strong className="SharkBody">Recommended for Better Evaluation</strong>
        <p className="SharkMuted SharkMt8">
          Applicants are strongly recommended to provide financial and business performance details for the last 5 years,
          as well as detailed 5-year future projections for Sales/Revenue, Gross Profit, Net Profit, Employees, and Offices.
          Providing complete historical and projected information will help IHN evaluate the business more effectively and
          help potential investors better understand the company's financial performance, growth, and future opportunity.
        </p>
        <p className="SharkMuted SharkMt8">
          All information and documents submitted will be used for verification, evaluation, and investment eligibility assessment.
        </p>
      </div>
    </>
  );
}

function BusinessRequirement({ formData, setFormData, errors }) {
  const set = (k,v) => setFormData({ ...formData, [k]: v });
  return (
    <>
      <SectionCard title="Funding Requirement">
        <FormGrid fields={FUNDING_FIELDS} data={formData} onChange={set} errors={errors} />
      </SectionCard>
      <SectionCard title="Use of Funds" hint="Percentages must total 100%.">
        <UseOfFundsTable rows={formData.useOfFunds || USE_OF_FUNDS_CATEGORIES.map(c=>({category:c}))} setRows={(r)=>set("useOfFunds", r)} />
      </SectionCard>
      <SectionCard title="Investor Proposition">
        <FormGrid fields={PROPOSITION_FIELDS} data={formData} onChange={set} errors={errors} />
      </SectionCard>
    </>
  );
}

/* ---------------------------------------------------------------------------
   LANDING / ROLE SELECTION SCREENS
--------------------------------------------------------------------------- */
function Landing({ onSelectRole }) {
  return (
    <div className="SharkSection SharkContainer SharkTextCenter">
      <h1 className="SharkH1">Shark Tank</h1>
      <p className="SharkBody SharkLandingIntro">
        A verified investment ecosystem operated by IHN, connecting cash and expertise
        with businesses seeking funding — built on verification, privacy, and quality opportunities.
      </p>
      <div className="SharkRoleGrid">
        <button type="button" className="SharkRoleCard SharkRoleCardCenter" onClick={()=>onSelectRole("invest")}>
          <img src={OpenInvestment} alt="Open to Invest" className="SharkLandingImg" />
          <div className="SharkEyebrow">Open to Invest</div>
           <h2 className="SharkH2 SharkRoleTitle">I want to invest, contribute expertise, or mentor businesses</h2>
          <div className="SharkMt24"><span className="SharkBtn SharkBtnPrimary">Proceed</span></div>
        </button>
        <button type="button" className="SharkRoleCard SharkRoleCardCenter" onClick={()=>onSelectRole("seek")}>
          <img src={InvetmentNeed} alt="Investment Needed" className="SharkLandingImg" />
          <div className="SharkEyebrow">Investment Needed</div>
          <h2 className="SharkH2 SharkRoleTitle">I am a business seeking funding, investment, or strategic support</h2>
          <div className="SharkMt24"><span className="SharkBtn SharkBtnPrimary">Proceed</span></div>
        </button>
      </div>
    </div>
  );
}

function InvestorTypeSelect({ onSelect, onBack }) {
  return (
      <div className="SharkSection SharkContainer">
      <button className="SharkBtn SharkBtnText SharkBackBtn SharkBackBtnAlignStart" onClick={onBack}>&#x276E;</button>
      <h2 className="SharkH2 SharkMt16 SharkTextCenter">Choose Your Investor Type</h2>
      <div className="SharkRoleGridThree">
        {INVESTOR_TYPE_OPTIONS.map(o => {
          const img = o.key === "cash" ? CashInvestorImg : o.key === "sweat" ? SweatInvestorImg : MentorImg;
          return (
            <button key={o.key} type="button" className="SharkRoleCard SharkRoleCardCenter" onClick={()=>onSelect(o.key)}>
              <img src={img} alt={o.title} className="SharkInvestorTypeImg" />
              <h3 className="SharkH3">{o.title}</h3>
              <p className="SharkMuted">{o.desc}</p>
              <div className="SharkMt24"><span className="SharkBtn SharkBtnPrimary SharkBtnSm">Select</span></div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   REVIEW / SUBMIT / STATUS SCREENS
--------------------------------------------------------------------------- */
function ReviewField({ label, value }) {
  const display = (value === "" || value === undefined || value === null) ? "N/A" : value;
  return (
    <div className="SharkReviewField">
      <span className="SharkReviewFieldLabel">{label}</span>
      <span className="SharkReviewFieldValue">{display}</span>
    </div>
  );
}

function ReviewSection({ title, fields, data, pastItems, storyItems }) {
  return (
    <div className="SharkCard SharkMt24 SharkReviewCard">
      <h3 className="SharkH3 SharkReviewCardTitle">{title}</h3>
      {fields.map(f => {
        const val = data[f.name];
        if (f.type === "select-other" && f.otherKey) {
          const otherVal = data[f.otherKey];
          let displayVal = val === "Other" ? (otherVal || "Other") : val;
          if (Array.isArray(displayVal)) displayVal = displayVal.length ? displayVal.join(", ") : undefined;
          return <ReviewField key={f.name} label={f.label} value={displayVal} />;
        }
        if (Array.isArray(val)) {
          return <ReviewField key={f.name} label={f.label} value={val.length ? val.join(", ") : undefined} />;
        }
        return <ReviewField key={f.name} label={f.label} value={val} />;
      })}
      {pastItems && pastItems.map((item, idx) => (
        <div key={idx} className="SharkReviewItem">
          <p className="SharkReviewItemTitle">Investment {idx + 1}</p>
          {PAST_INVESTMENT_FIELDS.map(f => (
            <ReviewField key={f.name} label={f.label} value={item[f.name]} />
          ))}
        </div>
      ))}
      {storyItems && storyItems.map((item, idx) => (
        <div key={idx} className="SharkReviewItem">
          <p className="SharkReviewItemTitle">Success Story {idx + 1}</p>
          {SUCCESS_STORY_FIELDS.map(f => (
            <ReviewField key={f.name} label={f.label} value={item[f.name]} />
          ))}
        </div>
      ))}
    </div>
  );
}

function ReviewSubmit({ title, onSubmit, agree, setAgree, formData, investorType, role }) {
  const na = "N/A";
  const declarations = investorType === "cash" ? CASH_INVESTOR_DECLARATIONS
    : investorType === "sweat" ? SWEAT_INVESTOR_DECLARATIONS
    : investorType === "mentor" ? MENTOR_DECLARATIONS : [];
  return (
    <div className="SharkContainer SharkSection">
      <h2 className="SharkH2 SharkTextCenter">Review & Submit</h2>
      <p className="SharkMuted SharkTextCenter">Please review your {title} before submitting to the IHN Team. You can return and edit at any time.</p>

      {role === "seek" && (
        <>
          <ReviewSection title="Business Basic Information" fields={BUSINESS_BASIC_FIELDS} data={formData} />
          <ReviewSection title="Partnership, Loans & Investment" fields={PARTNERSHIP_REVIEW_FIELDS} data={formData} />
          {formData.isPartnership === "Yes" && <ReviewSection title="Partnership Details" fields={PARTNERSHIP_DETAIL_FIELDS} data={formData} />}
          {formData.hasLoans === "Yes" && <ReviewSection title="Loans / Liabilities" fields={LOAN_FIELDS} data={formData} pastItems={formData.loans || []} />}
          {formData.hasExistingInvestment === "Yes" && <ReviewSection title="Existing Investors" fields={EXISTING_INVESTOR_FIELDS} data={formData} pastItems={formData.existingInvestors || []} />}
          {formData.inNegotiation === "Yes" && <ReviewSection title="Investment Negotiation" fields={NEGOTIATION_FIELDS} data={formData} />}
          <ReviewSection title="Future 5-Year Projection" fields={PROJECTION_REVIEW_FIELDS} data={formData} />
          <ReviewSection title="Current Business Operations" fields={CURRENT_OPS_FIELDS} data={formData} />
          <div className="SharkReviewGrid">
            <ReviewSection title="Management Team" fields={MANAGEMENT_FIELDS} data={formData} />
            <ReviewSection title="Funding Requirement" fields={FUNDING_FIELDS} data={formData} />
          </div>
          <div className="SharkReviewGrid">
            <ReviewSection title="Investor Proposition" fields={PROPOSITION_FIELDS} data={formData} />
            <ReviewSection title="Legal / Regulatory Information" fields={LEGAL_FIELDS} data={formData} />
          </div>
        </>
      )}

      {role === "invest" && investorType === "cash" && (
        <>
          <div className="SharkReviewGrid">
            <ReviewSection title="Personal Information" fields={PERSONAL_FIELDS()} data={formData} />
            <ReviewSection title="Investment Details"
              fields={INVESTMENT_DETAILS_REVIEW_FIELDS}
              data={formData}
              pastItems={formData.hasPastInvestments === "Yes" ? (formData.pastInvestments || []) : []}
              storyItems={formData.successStories || []} />
          </div>
          <ReviewSection title="Current Portfolio & Strategic Support" fields={PORTFOLIO_FIELDS} data={formData} />
        </>
      )}

      {role === "invest" && investorType === "sweat" && (
        <>
          <div className="SharkReviewGrid">
            <ReviewSection title="Personal Information" fields={SWEAT_PERSONAL_REVIEW_FIELDS()} data={formData} />
            <ReviewSection title="Contribution Details" fields={SWEAT_CONTRIBUTION_REVIEW_FIELDS} data={formData} />
          </div>
        </>
      )}

      {role === "invest" && investorType === "mentor" && (
        <>
          <div className="SharkReviewGrid">
            <ReviewSection title="Personal Information" fields={MENTOR_PERSONAL_REVIEW_FIELDS()} data={formData} />
            <ReviewSection title="Mentoring Details" fields={MENTORING_REVIEW_FIELDS} data={formData} />
          </div>
          {formData.successStories && formData.successStories.length > 0 && (
            <div className="SharkCard SharkMt24">
              <h3 className="SharkH3 SharkReviewCardTitle">Professional / Business Success Stories</h3>
              {formData.successStories.map((story, idx) => (
                <div key={idx} className="SharkReviewItem">
                  {story.company && <ReviewField label="Company Name" value={story.company} />}
                  {story.year && <ReviewField label="Year" value={story.year} />}
                  {story.location && <ReviewField label="Location" value={story.location} />}
                  {story.story && <ReviewField label="Story" value={story.story} />}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {declarations.length > 0 && (
        <div className="SharkCard SharkMt24">
          <h3 className="SharkH3 SharkReviewCardTitle">Declaration & Consent</h3>
          <ol className="SharkDeclList">
            {declarations.map((d, i) => (
              <li key={i} className="SharkDeclItem">{d}</li>
            ))}
          </ol>
          <div className="SharkAgreeRow">
            <input type="checkbox" id="agree" className="SharkCheckbox" checked={agree} onChange={(e)=>setAgree(e.target.checked)} />
            <label htmlFor="agree" className="SharkBody">
              I confirm the information is accurate and may be reviewed by IHN. <strong>I agree</strong>
            </label>
          </div>
        </div>
      )}

      <div className="SharkMt24 SharkTextCenter">
        <button className="SharkBtn SharkBtnPrimary" disabled={!agree} onClick={onSubmit}>Submit to IHN Team</button>
      </div>
    </div>
  );
}

function StatusScreen({ state, onDecision, roleLabel }) {
  return (
    <div className="SharkContainer SharkSection SharkStatusWrap">
      <h2 className="SharkH2 SharkTextCenter">
        {state === "Access Granted" ? "Access Granted" : state === "Rejected" ? "Application Not Approved" : "Submission Received"}
      </h2>
      {state !== "Access Granted" && state !== "Rejected" && (
        <p className="SharkBody SharkTextCenter">Your information has been submitted to the IHN Team for verification and evaluation.</p>
      )}
      {state === "Access Granted" && (
        <p className="SharkBody SharkTextCenter">Your {roleLabel} profile has been verified by the IHN Team.</p>
      )}
      {state === "Rejected" && (
        <p className="SharkBody SharkTextCenter">The IHN Team was unable to verify this submission at this time. You may revise and resubmit.</p>
      )}
      <div className="SharkTextCenter">
        <span className="SharkCardFlat SharkMt24 SharkStatusInline">
          <strong>Status:</strong> {state}
        </span>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   PORTALS
--------------------------------------------------------------------------- */
function OpportunityCard({ opp, interested, onInterest }) {
  return (
    <div className="SharkOppCard">
      <div className="SharkFlexBetween">
        <Avatar name={opp.name} />
        <Badge state={opp.status} />
      </div>
      <h3 className="SharkH3">{opp.name}</h3>
      <p className="SharkMuted">{opp.industry} · {opp.location}</p>
      <p className="SharkBody">{opp.description}</p>
      <div className="SharkFlexBetween">
        <span className="SharkBody"><strong>{opp.funding}</strong> for {opp.equity}</span>
        <span className="SharkMuted">Posted {opp.posted}</span>
      </div>
      <div className="SharkFlexBetween SharkMt8">
        <button className="SharkBtn SharkBtnSecondary SharkBtnSm">Review Documents</button>
        <button className="SharkBtn SharkBtnPrimary SharkBtnSm" disabled={interested} onClick={onInterest}>
          {interested ? "Interest Sent" : "Express Interest"}
        </button>
      </div>
    </div>
  );
}

function InvestorPortal({ profile, badgeGranted, interests, setInterests }) {
  const [tab, setTab] = useState("opportunities");
  const [filters, setFilters] = useState({ category:"", industry:"", location:"", sort:"latest" });
  const filtered = useMemo(() => {
    let list = MOCK_OPPORTUNITIES.filter(o =>
      (!filters.category || o.category === filters.category) &&
      (!filters.industry || o.industry === filters.industry) &&
      (!filters.location || o.location.toLowerCase().includes(filters.location.toLowerCase())));
    if (filters.sort === "latest") list = list.slice().sort((a,b)=> new Date(b.posted)-new Date(a.posted));
    return list;
  }, [filters]);

  return (
    <div className="SharkContainer SharkSection">
      <div className="SharkFlexBetween">
        <div>
          <div className="SharkEyebrow">Investor Portal</div>
          <h2 className="SharkH2">Welcome, {profile.fullName || "Investor"}</h2>
        </div>
        {badgeGranted && <span className="SharkBadge SharkBadgeInvest">Open to Invest</span>}
      </div>
      <div className="SharkPortalShell SharkMt24">
        <div className="SharkPortalNav">
          {INVESTOR_PORTAL_NAV.map(([k,l]) => (
            <button key={k} className={"SharkPortalNavItem"+(tab===k?" SharkActive":"")} onClick={()=>setTab(k)}>{l}</button>
          ))}
        </div>
        <div>
          {tab === "opportunities" && (
            <>
              <div className="SharkCardFlat">
                <div className="SharkFormGrid">
                  <Field field={{name:"category",label:"Category",type:"select",options:CATEGORIES}} value={filters.category} onChange={(v)=>setFilters({...filters,category:v})} />
                  <Field field={{name:"industry",label:"Industry",type:"select",options:INDUSTRIES}} value={filters.industry} onChange={(v)=>setFilters({...filters,industry:v})} />
                  <Field field={{name:"location",label:"Location",type:"text"}} value={filters.location} onChange={(v)=>setFilters({...filters,location:v})} />
                  <Field field={{name:"sort",label:"Sort",type:"select",options:["latest"]}} value={filters.sort} onChange={(v)=>setFilters({...filters,sort:v})} />
                </div>
              </div>
              <div className="SharkOppGrid SharkMt24">
                {filtered.map(o => (
                  <OpportunityCard key={o.id} opp={o} interested={!!interests[o.id]} onInterest={()=>setInterests({...interests,[o.id]:true})} />
                ))}
              </div>
            </>
          )}
          {tab === "interests" && (
            <ul className="SharkListPlain">
              {Object.keys(interests).length === 0 && <p className="SharkMuted">You haven't expressed interest in any opportunity yet.</p>}
              {MOCK_OPPORTUNITIES.filter(o=>interests[o.id]).map(o => (
                <li key={o.id} className="SharkCardFlat SharkFlexBetween">
                  <span>{o.name}</span><Badge state="Interest Sent" />
                </li>
              ))}
            </ul>
          )}
          {tab === "portfolio" && (
            <div className="SharkCardFlat">
              <p className="SharkBody"><strong>Active Investments:</strong> {profile.activeInvestments || 0}</p>
              <p className="SharkBody"><strong>Exited Investments:</strong> {profile.exitedInvestments || 0}</p>
              <p className="SharkBody"><strong>Portfolio Companies:</strong> {profile.portfolioCompanies || "—"}</p>
            </div>
          )}
          {tab === "profile" && (
            <div className="SharkCardFlat">
              <p className="SharkBody"><strong>{profile.fullName}</strong></p>
              <p className="SharkMuted">{profile.designation} {profile.company ? `· ${profile.company}` : ""}</p>
              <p className="SharkBody SharkMt8">{profile.expertise}</p>
            </div>
          )}
          {(tab === "messages" || tab === "notifications" || tab === "settings") && (
            <div className="SharkCardFlat"><p className="SharkMuted">No {tab} yet.</p></div>
          )}
        </div>
      </div>
    </div>
  );
}

function SweatMentorPortal({ profile, kind }) {
  const [tab, setTab] = useState("opportunities");
  const [interests, setInterests] = useState({});
  const nav = kind === "mentor" ? MENTOR_PORTAL_NAV : SWEAT_PORTAL_NAV;
  return (
    <div className="SharkContainer SharkSection">
      <div className="SharkEyebrow">{kind === "mentor" ? "Mentor Portal" : "Sweat Investor Portal"}</div>
      <h2 className="SharkH2">Welcome, {profile.fullName || (kind === "mentor" ? "Mentor" : "Sweat Investor")}</h2>
      <div className="SharkPortalShell SharkMt24">
        <div className="SharkPortalNav">
          {nav.map(([k,l]) => <button key={k} className={"SharkPortalNavItem"+(tab===k?" SharkActive":"")} onClick={()=>setTab(k)}>{l}</button>)}
        </div>
        <div>
          {tab === "opportunities" && (
            <div className="SharkOppGrid">
              {MOCK_OPPORTUNITIES.map(o => (
                <OpportunityCard key={o.id} opp={o} interested={!!interests[o.id]} onInterest={()=>setInterests({...interests,[o.id]:true})} />
              ))}
            </div>
          )}
          {tab === "network" && (
            <div className="SharkOppGrid">
              {MOCK_MENTORS.map(m => (
                <div key={m.name} className="SharkOppCard">
                  <Avatar name={m.name} />
                  <h3 className="SharkH3">{m.name}</h3>
                  <p className="SharkMuted">{m.industry} · {m.years} yrs experience</p>
                  <p className="SharkBody">{m.expertise}</p>
                </div>
              ))}
              <div className="SharkOppCard">
                <Avatar name={profile.fullName || "You"} />
                <h3 className="SharkH3">{profile.fullName || "Your Profile"}</h3>
                <p className="SharkMuted">{profile.industry} · {profile.experience || 0} yrs experience</p>
                <p className="SharkBody">{profile.expertise}</p>
                <p className="SharkMuted">{profile.successStories ? profile.successStories.slice(0,120)+"…" : ""}</p>
              </div>
            </div>
          )}
          {tab === "profile" && (
            <div className="SharkCardFlat">
              <p className="SharkBody"><strong>{profile.fullName}</strong></p>
              <p className="SharkMuted">{profile.designation} {profile.company ? `· ${profile.company}` : ""}</p>
              <p className="SharkBody SharkMt8">{profile.expertise}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BusinessPortal({ business }) {
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({ visibility:"all" });
  const set = (k,v) => setDraft({ ...draft, [k]: v });

  const createPost = () => {
    setPosts([{ ...draft, id: uid(), interest: 0, status:"Live" }, ...posts]);
    setDraft({ visibility:"all" });
    setShowForm(false);
  };

  return (
    <div className="SharkContainer SharkSection">
      <div className="SharkEyebrow">Business / Investment Needed Portal</div>
      <h2 className="SharkH2">Welcome, {business.legalName || "Business Owner"}</h2>

      <div className="SharkCardFlat SharkMt16">
        <strong>You Cannot View Other Businesses' Investment Posts</strong>
        <p className="SharkMuted SharkMt8">Your current profile is in Investment Needed mode. You can create and manage your own funding requirements.</p>
      </div>

      <div className="SharkFlexBetween SharkMt24">
        <h3 className="SharkH3">My Funding Posts</h3>
        <button className="SharkBtn SharkBtnPrimary" onClick={()=>setShowForm(!showForm)}>{showForm ? "Cancel" : "Create Investment Post"}</button>
      </div>

      {showForm && (
        <div className="SharkCard SharkMt16">
          <FormGrid fields={[
            { name:"title", label:"Post Title", type:"text", required:true, span2:true },
            { name:"category", label:"Category", type:"select", options:CATEGORIES },
            { name:"industry", label:"Industry", type:"select", options:INDUSTRIES },
            { name:"fundingAmount", label:"Funding Amount", type:"number" },
            { name:"equityOffered", label:"Equity Offered", type:"text" },
            { name:"description", label:"Description", type:"textarea", span2:true },
            { name:"businessDetails", label:"Business Details", type:"textarea", span2:true },
            { name:"targetInvestorType", label:"Target Investor Type", type:"select", options:["Cash Investor","Sweat Investor","Mentor","Any"] },
            { name:"targetGeography", label:"Target Geography", type:"text" },
          ]} data={draft} onChange={set} errors={{}} />
          <div className="SharkFormGrid SharkMt16">
            <FileUpload label="Pitch Deck" fileName={draft.pitchDeck} onChange={(n)=>set("pitchDeck",n)} onRemove={()=>set("pitchDeck","")} />
            <FileUpload label="Business Documents" fileName={draft.bizDocs} onChange={(n)=>set("bizDocs",n)} onRemove={()=>set("bizDocs","")} />
          </div>
          <div className="SharkMt16">
            <Field field={{name:"visibility",label:"Post Privacy",type:"radio",options:["View for All Categories","Hide from Specific Investors","Hide from Specific Categories"]}}
              value={draft.visibility === "all" ? "View for All Categories" : draft.visibility}
              onChange={(v)=>set("visibility", v)} />
            {draft.visibility === "Hide from Specific Investors" &&
              <Field field={{name:"hiddenInvestors",label:"Investors who cannot see this post",type:"text",span2:true}} value={draft.hiddenInvestors} onChange={(v)=>set("hiddenInvestors",v)} />}
            {draft.visibility === "Hide from Specific Categories" &&
              <Field field={{name:"hiddenCategories",label:"Categories hidden from",type:"multiselect",options:["Cash Investor","Sweat Investor","Mentor"]}} value={draft.hiddenCategories} onChange={(v)=>set("hiddenCategories",v)} />}
          </div>
          <div className="SharkMt16"><button className="SharkBtn SharkBtnPrimary" onClick={createPost}>Save Changes</button></div>
        </div>
      )}

      <div className="SharkMt24">
        {posts.length === 0 && <p className="SharkMuted">No funding posts yet. Create your first post above.</p>}
        {posts.map(p => (
          <div key={p.id} className="SharkCardFlat SharkMt16">
            <div className="SharkFlexBetween">
              <strong>{p.title || "Untitled Post"}</strong>
              <Badge state="Verified" />
            </div>
            <p className="SharkMuted">{p.industry} · {p.category}</p>
            <p className="SharkBody">{p.description}</p>
            <div className="SharkFlexBetween SharkMt8">
              <span className="SharkMuted">People viewed: {p.interest}</span>
              <button className="SharkBtn SharkBtnSecondary SharkBtnSm">Edit Post</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   DEV CONTROLS
--------------------------------------------------------------------------- */
function DevControls({ role, setRole, investorType, setInvestorType, verification, setVerification, resetAll }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="SharkDev">
      <div className="SharkFlexBetween">
        <div className="SharkDevTitle">Development Demo Controls</div>
        <button className="SharkBtn SharkBtnText SharkBtnSm" onClick={()=>setOpen(!open)}>{open ? "Hide" : "Show"}</button>
      </div>
      {open && (
        <>
          <label className="SharkHint">Switch Role</label>
          <select className="SharkSelect" value={role||""} onChange={(e)=>setRole(e.target.value)}>
            <option value="">—</option>
            <option value="invest">Open to Invest</option>
            <option value="seek">Investment Needed</option>
          </select>
          {role === "invest" && (
            <>
              <label className="SharkHint">Investor Type</label>
              <select className="SharkSelect" value={investorType||""} onChange={(e)=>setInvestorType(e.target.value)}>
                <option value="">—</option>
                <option value="cash">Cash Investor</option>
                <option value="sweat">Sweat Investor</option>
                <option value="mentor">Mentor</option>
              </select>
            </>
          )}
          <label className="SharkHint">Set Verification</label>
          <select className="SharkSelect" value={verification} onChange={(e)=>setVerification(e.target.value)}>
            {VERIFICATION_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="SharkBtn SharkBtnPrimary SharkBtnSm" onClick={()=>setVerification("Access Granted")}>Grant Access</button>
          <button className="SharkBtn SharkBtnSecondary SharkBtnSm" onClick={resetAll}>Reset Flow</button>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   MAIN APP
--------------------------------------------------------------------------- */
export default function SharkTank() {
  const [step, setStep] = useState("landing"); // landing | typeSelect | form | review | status | portal
  const [role, setRole] = useState("");          // invest | seek
  const [investorType, setInvestorType] = useState(""); // cash | sweat | mentor
  const [verification, setVerification] = useState("Draft");

  const [cashData, setCashData] = useState({ pastInvestments: [], successStories: [] });
  const [sweatData, setSweatData] = useState({ pastExperience: [], successStories: [] });
  const [mentorData, setMentorData] = useState({ successStories: [] });
  const [businessData, setBusinessData] = useState({ loans: [], existingInvestors: [], ownership: {}, useOfFunds: USE_OF_FUNDS_CATEGORIES.map(c=>({category:c})) });

  const [agree, setAgree] = useState(false);
  const [references, setReferences] = useState("");
  const [interests, setInterests] = useState({});

  const resetAll = () => {
    setStep("landing"); setRole(""); setInvestorType(""); setVerification("Draft");
    setCashData({ pastInvestments: [], successStories: [] });     setSweatData({ pastExperience: [], successStories: [] });     setMentorData({ successStories: [] });
    setBusinessData({ loans: [], existingInvestors: [], ownership: {}, useOfFunds: USE_OF_FUNDS_CATEGORIES.map(c=>({category:c})) });
    setAgree(false); setReferences(""); setInterests({});
  };

  const currentFormData = role === "seek" ? businessData
    : investorType === "cash" ? cashData
    : investorType === "sweat" ? sweatData
    : mentorData;
  const setCurrentFormData = role === "seek" ? setBusinessData
    : investorType === "cash" ? setCashData
    : investorType === "sweat" ? setSweatData
    : setMentorData;

  const errors = {}; // inline required-field validation is handled per-field via Field component's `required` styling

  const handleSelectRole = (r) => {
    setRole(r);
    if (r === "seek") { setStep("form"); setVerification("Draft"); }
    else { setStep("typeSelect"); }
  };
  const handleSelectInvestorType = (t) => { setInvestorType(t); setStep("details"); setVerification("Draft"); };

  const roleLabel = role === "seek" ? "Business" : investorType === "cash" ? "Cash Investor" : investorType === "sweat" ? "Sweat Investor" : "Mentor";

  const handleSubmit = () => {
    setVerification("Submitted");
    setStep("status");
    setTimeout(() => setVerification((v)=> v === "Submitted" ? "Under Review" : v), 1200);
  };

  let content;
  if (step === "landing") {
    content = <Landing onSelectRole={handleSelectRole} />;
  } else if (step === "typeSelect") {
    content = <InvestorTypeSelect onSelect={handleSelectInvestorType} onBack={()=>setStep("landing")} />;
  } else if (step === "details") {
    const nextStep = investorType === "cash" ? "preferences" : "contribution";
    const step2Label = investorType === "cash" ? "Preferences" : investorType === "sweat" ? "Contribution" : "Contribution";
    content = (
      <div className="SharkContainer SharkSection">
        <button className="SharkBtn SharkBtnText SharkBackBtn SharkBackBtnAlignStart" onClick={()=> setStep("typeSelect")}>&#x276E;</button>
        <div className="SharkStepper SharkMt16">
          <span className="SharkStepChip SharkActive">Details</span>
          <span className="SharkStepChip">{step2Label}</span>
          <span className="SharkStepChip">Review</span>
          <span className="SharkStepChip">Verification</span>
        </div>
        <h2 className="SharkH2 SharkTextCenter">{roleLabel} Verification Form</h2>
        {role === "invest" && investorType === "cash" && <CashInvestorDetails formData={cashData} setFormData={setCashData} errors={errors} />}
        {role === "invest" && investorType === "sweat" && <SweatInvestorDetails formData={sweatData} setFormData={setSweatData} errors={errors} />}
        {role === "invest" && investorType === "mentor" && <MentorDetails formData={mentorData} setFormData={setMentorData} errors={errors} />}
        <div className="SharkMt24 SharkTextCenter">
          <button className="SharkBtn SharkBtnPrimary" onClick={()=>setStep(nextStep)}>Continue</button>
        </div>
      </div>
    );
  } else if (step === "preferences" || step === "contribution") {
    const step2Label = investorType === "cash" ? "Preferences" : investorType === "sweat" ? "Contribution" : "Contribution";
    content = (
      <div className="SharkContainer SharkSection">
        <button className="SharkBtn SharkBtnText SharkBackBtn SharkBackBtnAlignStart" onClick={()=> setStep("details")}>&#x276E;</button>
        <div className="SharkStepper SharkMt16">
          <span className="SharkStepChip">Details</span>
          <span className="SharkStepChip SharkActive">{step2Label}</span>
          <span className="SharkStepChip">Review</span>
          <span className="SharkStepChip">Verification</span>
        </div>
        <h2 className="SharkH2 SharkTextCenter">{roleLabel} Verification Form</h2>
        {role === "invest" && investorType === "cash" && <CashInvestorPreferences formData={cashData} setFormData={setCashData} errors={errors} />}
        {role === "invest" && investorType === "sweat" && <SweatInvestorContribution formData={sweatData} setFormData={setSweatData} errors={errors} />}
        {role === "invest" && investorType === "mentor" && <MentorContribution formData={mentorData} setFormData={setMentorData} errors={errors} />}
        <div className="SharkMt24 SharkTextCenter">
          <button className="SharkBtn SharkBtnPrimary" onClick={()=>setStep("review")}>Continue</button>
        </div>
      </div>
    );
  } else if (step === "form") {
    const step2Label = role === "seek" ? "Status" : investorType === "cash" ? "Preferences" : "Contribution";
    content = (
      <div className="SharkContainer SharkSection">
        <button className="SharkBtn SharkBtnText SharkBackBtn SharkBackBtnAlignStart" onClick={()=> role==="seek" ? setStep("landing") : setStep("typeSelect")}>&#x276E;</button>
        <div className="SharkStepper SharkMt16">
          <span className="SharkStepChip SharkActive">Details</span>
          <span className="SharkStepChip">{step2Label}</span>
          {role === "seek" && <>
            <span className="SharkStepChip">History</span>
            <span className="SharkStepChip">Documents</span>
            <span className="SharkStepChip">Requirement</span>
          </>}
          <span className="SharkStepChip">Review</span>
          <span className="SharkStepChip">Verification</span>
        </div>
        <h2 className="SharkH2 SharkTextCenter">{roleLabel} Verification Form</h2>
        {role === "seek" && <BusinessDetails formData={businessData} setFormData={setBusinessData} errors={errors} />}
        {role === "invest" && investorType === "sweat" && <SweatInvestorDetails formData={sweatData} setFormData={setSweatData} errors={errors} />}
        {role === "invest" && investorType === "mentor" && <MentorDetails formData={mentorData} setFormData={setMentorData} errors={errors} />}
        <div className="SharkMt24 SharkTextCenter">
          <button className="SharkBtn SharkBtnPrimary" onClick={()=>setStep(role === "seek" ? "bizStatus" : "review")}>Continue</button>
        </div>
      </div>
    );
  } else if (step === "bizStatus") {
    content = (
      <div className="SharkContainer SharkSection">
        <button className="SharkBtn SharkBtnText SharkBackBtn" onClick={()=>setStep("form")}>&#x276E;</button>
        <div className="SharkStepper SharkMt16">
          <span className="SharkStepChip">Details</span>
          <span className="SharkStepChip SharkActive">Status</span>
          <span className="SharkStepChip">History</span>
          <span className="SharkStepChip">Documents</span>
          <span className="SharkStepChip">Requirement</span>
          <span className="SharkStepChip">Review</span>
          <span className="SharkStepChip">Verification</span>
        </div>
        <h2 className="SharkH2 SharkTextCenter">{roleLabel} Verification Form</h2>
        <BusinessStatus formData={businessData} setFormData={setBusinessData} errors={errors} />
        <div className="SharkMt24 SharkTextCenter">
          <button className="SharkBtn SharkBtnPrimary" onClick={()=>setStep("history")}>Continue</button>
        </div>
      </div>
    );
  } else if (step === "history") {
    content = (
      <div className="SharkContainer SharkSection">
        <button className="SharkBtn SharkBtnText SharkBackBtn" onClick={()=>setStep("form")}>&#x276E;</button>
        <div className="SharkStepper SharkMt16">
          <span className="SharkStepChip">Details</span>
          <span className="SharkStepChip">Status</span>
          <span className="SharkStepChip SharkActive">History</span>
          <span className="SharkStepChip">Documents</span>
          <span className="SharkStepChip">Requirement</span>
          <span className="SharkStepChip">Review</span>
          <span className="SharkStepChip">Verification</span>
        </div>
        <h2 className="SharkH2 SharkTextCenter">{roleLabel} Verification Form</h2>
        <BusinessHistory formData={businessData} setFormData={setBusinessData} errors={errors} />
        <div className="SharkMt24 SharkTextCenter">
          <button className="SharkBtn SharkBtnPrimary" onClick={()=>setStep("documents")}>Continue</button>
        </div>
      </div>
    );
  } else if (step === "documents") {
    content = (
      <div className="SharkContainer SharkSection">
        <button className="SharkBtn SharkBtnText SharkBackBtn" onClick={()=>setStep("history")}>&#x276E;</button>
        <div className="SharkStepper SharkMt16">
          <span className="SharkStepChip">Details</span>
          <span className="SharkStepChip">Status</span>
          <span className="SharkStepChip">History</span>
          <span className="SharkStepChip SharkActive">Documents</span>
          <span className="SharkStepChip">Requirement</span>
          <span className="SharkStepChip">Review</span>
          <span className="SharkStepChip">Verification</span>
        </div>
        <h2 className="SharkH2 SharkTextCenter">{roleLabel} Verification Form</h2>
        <BusinessDocuments formData={businessData} setFormData={setBusinessData} errors={errors} />
        <div className="SharkMt24 SharkTextCenter">
          <button className="SharkBtn SharkBtnPrimary" onClick={()=>setStep("requirement")}>Continue</button>
        </div>
      </div>
    );
  } else if (step === "requirement") {
    content = (
      <div className="SharkContainer SharkSection">
        <button className="SharkBtn SharkBtnText SharkBackBtn" onClick={()=>setStep("documents")}>&#x276E;</button>
        <div className="SharkStepper SharkMt16">
          <span className="SharkStepChip">Details</span>
          <span className="SharkStepChip">Status</span>
          <span className="SharkStepChip">History</span>
          <span className="SharkStepChip">Documents</span>
          <span className="SharkStepChip SharkActive">Requirement</span>
          <span className="SharkStepChip">Review</span>
          <span className="SharkStepChip">Verification</span>
        </div>
        <h2 className="SharkH2 SharkTextCenter">{roleLabel} Verification Form</h2>
        <BusinessRequirement formData={businessData} setFormData={setBusinessData} errors={errors} />
        <div className="SharkMt24 SharkTextCenter">
          <button className="SharkBtn SharkBtnPrimary" onClick={()=>setStep("review")}>Continue</button>
        </div>
      </div>
    );
  } else if (step === "review") {
    content = (
      <div className="SharkContainer SharkSection">
        <button className="SharkBtn SharkBtnText SharkBackBtn" onClick={()=>setStep(role === "seek" ? "requirement" : investorType === "cash" ? "preferences" : "contribution")}>&#x276E;</button>
        <ReviewSubmit title={`${roleLabel} Verification Form`} onSubmit={handleSubmit}
          agree={agree} setAgree={setAgree}
          formData={currentFormData} investorType={investorType} role={role} />
      </div>
    );
  } else if (step === "status") {
    if (verification === "Access Granted") {
      content = (
        <div>
          <StatusScreen state={verification} roleLabel={roleLabel} />
          <div className="SharkTextCenter">
            <button className="SharkBtn SharkBtnPrimary" onClick={()=>setStep("portal")}>Enter {roleLabel} Portal</button>
          </div>
        </div>
      );
    } else {
      content = <StatusScreen state={verification} roleLabel={roleLabel} />;
    }
  } else if (step === "portal") {
    if (role === "seek") content = <BusinessPortal business={businessData} />;
    else if (investorType === "cash") content = <InvestorPortal profile={cashData} badgeGranted={verification==="Access Granted"} interests={interests} setInterests={setInterests} />;
    else if (investorType === "sweat") content = <SweatMentorPortal profile={sweatData} kind="sweat" />;
    else content = <SweatMentorPortal profile={mentorData} kind="mentor" />;
  }

  return (
    <div className="SharkRoot">
      {content}
      <DevControls
        role={role} setRole={(r)=>{ setRole(r); setStep(r==="seek" ? "form" : "typeSelect"); }}
        investorType={investorType} setInvestorType={(t)=>{ setInvestorType(t); setStep("form"); }}
        verification={verification} setVerification={(v)=>{ setVerification(v); if (v !== "Draft") setStep("status"); }}
        resetAll={resetAll}
      />
    </div>
  );
}
